/**
 * Sage Code Sync Panel
 *
 * Visibility surface for the Role Master → F&O ACCSITitleCode cascade.
 * For every RM entry with a populated sage_title_code, shows the diff
 * against the current ACCSITitleCode value on every F&O Title that the
 * entry targets (both its direct fo_title_id and the TitleId of any linked
 * Job via Positions).
 *
 * Actions:
 *   - Per-row "Force Sync": pushes just that RM's sage code
 *   - Bulk "Sync all out-of-sync": pushes every entry currently drifted
 *   - Refresh: re-fetches the diff (bypassing the frontend cache; F&O side
 *     has a 60s SharedCache)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, HelpCircle, Link2Off, Loader2, RefreshCw, Search, Zap,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import roleMasterService, {
  type SageCodeSyncEntry,
  type SageCodeSyncResponse,
  type SageCodeSyncState,
} from '@/services/api/roleMasterService';

// ── Status presentation ──────────────────────────────────────────────

const STATE_PRESET: Record<SageCodeSyncState, { label: string; tone: string; dot: string; icon: React.ReactNode }> = {
  in_sync:     { label: 'In sync',     tone: 'text-emerald-700 bg-emerald-100', dot: 'bg-emerald-500', icon: <CheckCircle2 className="h-3 w-3" /> },
  out_of_sync: { label: 'Out of sync', tone: 'text-red-700 bg-red-100',         dot: 'bg-red-500',     icon: <AlertCircle className="h-3 w-3" /> },
  no_link:     { label: 'No linked Title', tone: 'text-gray-700 bg-gray-100',   dot: 'bg-gray-400',    icon: <Link2Off className="h-3 w-3" /> },
  unknown:     { label: 'Unknown',     tone: 'text-gray-500 bg-gray-50',        dot: 'bg-gray-300',    icon: <HelpCircle className="h-3 w-3" /> },
};

// ── Component ────────────────────────────────────────────────────────

export interface SageCodeSyncPanelProps {
  /** If provided, the panel scrolls the matching row into view and expands it on mount. */
  focusEntryId?: number;
}

const SageCodeSyncPanel: React.FC<SageCodeSyncPanelProps> = ({ focusEntryId }) => {
  const [data, setData] = useState<SageCodeSyncResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const [pushingEntryId, setPushingEntryId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | SageCodeSyncState>('all');
  const [search, setSearch] = useState('');
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<number>>(new Set());
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);

  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});

  // Load / reload
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await roleMasterService.getSageCodeSyncStatus();
      setData(resp);
    } catch (e: any) {
      console.error(e);
      setBanner({ tone: 'error', message: `Failed to load sync status: ${e?.message || 'unknown error'}` });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Focus handling — scroll the target row into view and expand it
  useEffect(() => {
    if (!focusEntryId || !data) return;
    const el = rowRefs.current[focusEntryId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setExpandedEntryIds(prev => new Set(prev).add(focusEntryId));
    }
  }, [focusEntryId, data]);

  // Filtered view
  const filteredEntries = useMemo<SageCodeSyncEntry[]>(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return data.entries.filter(e => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (!term) return true;
      return (
        e.role_code.toLowerCase().includes(term) ||
        e.title.toLowerCase().includes(term) ||
        e.rm_sage_code.toLowerCase().includes(term) ||
        e.linked_titles.some(t => t.title_id.toLowerCase().includes(term))
      );
    });
  }, [data, search, statusFilter]);

  const toggleExpanded = (entryId: number) => {
    setExpandedEntryIds(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  // Summarise "updated + created" as the user-facing count — the backend
  // distinguishes PATCH (existing title) vs POST (created title), but both
  // mean "sage code is now in F&O".
  const totalPushed = (resp: { updated: number; created: number }) => resp.updated + resp.created;

  // Actions
  const handleForceSyncOne = async (entryId: number) => {
    setPushingEntryId(entryId);
    setBanner(null);
    try {
      const resp = await roleMasterService.pushSageCodeForEntry(entryId);
      const errs = resp.errors;
      const pushed = totalPushed(resp);
      if (errs > 0) {
        setBanner({ tone: 'error', message: `Pushed ${pushed} F&O Title(s) with ${errs} error(s) — see details below.` });
      } else {
        const breakdown = resp.created > 0 && resp.updated > 0
          ? ` (${resp.updated} updated, ${resp.created} created)`
          : resp.created > 0
            ? ' (created new Title in F&O)'
            : '';
        setBanner({ tone: 'success', message: `Synced ${pushed} F&O Title(s)${breakdown}.` });
      }
      await load();
    } catch (e: any) {
      setBanner({ tone: 'error', message: `Push failed: ${e?.message || 'unknown error'}` });
    } finally {
      setPushingEntryId(null);
    }
  };

  const handleSyncAllOutOfSync = async () => {
    if (!data) return;
    const ids = data.entries.filter(e => e.status === 'out_of_sync').map(e => e.entry_id);
    if (ids.length === 0) return;
    setIsPushing(true);
    setBanner(null);
    try {
      const resp = await roleMasterService.pushSageCodesForEntries(ids);
      const pushed = totalPushed(resp);
      if (resp.errors > 0) {
        setBanner({ tone: 'error', message: `Pushed ${pushed} F&O Title(s) with ${resp.errors} error(s).` });
      } else {
        const breakdown = resp.created > 0 && resp.updated > 0
          ? ` (${resp.updated} updated, ${resp.created} created)`
          : resp.created > 0
            ? ` (${resp.created} created in F&O)`
            : '';
        setBanner({ tone: 'success', message: `Synced ${pushed} F&O Title(s) across ${resp.total_entries} role(s)${breakdown}.` });
      }
      await load();
    } catch (e: any) {
      setBanner({ tone: 'error', message: `Bulk push failed: ${e?.message || 'unknown error'}` });
    } finally {
      setIsPushing(false);
    }
  };

  // Render
  const stats = data?.stats;
  const outOfSyncCount = stats?.out_of_sync ?? 0;

  return (
    <div className="space-y-4">
      {/* Header + bulk action */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Sage Title Code Sync</h2>
          <p className="text-sm text-gray-500">
            Compares Role Master <code className="px-1 py-0.5 rounded bg-gray-100 text-xs">sage_title_code</code>{' '}
            against F&O <code className="px-1 py-0.5 rounded bg-gray-100 text-xs">HcmTitle.ACCSITitleCode</code>{' '}
            for every linked Title — both directly via RM and via linked Jobs.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => load()} disabled={isLoading}>
            <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleSyncAllOutOfSync}
            disabled={isPushing || outOfSyncCount === 0 || !data}
          >
            {isPushing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            Sync all out-of-sync ({outOfSyncCount})
          </Button>
        </div>
      </div>

      {/* Inline banner (replaces alert popups) */}
      {banner && (
        <div
          className={cn(
            'flex items-start gap-3 px-3 py-2 rounded border text-sm',
            banner.tone === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800',
          )}
        >
          <div className="flex-1">{banner.message}</div>
          <button type="button" onClick={() => setBanner(null)} className="text-xs opacity-70 hover:opacity-100">
            Dismiss
          </button>
        </div>
      )}

      {/* F&O fetch error — shown when the integration-service call failed */}
      {data?.fetch_error && (
        <div className="flex items-start gap-3 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">F&amp;O sync status is unavailable</div>
            <div className="text-xs text-amber-800 mt-0.5 font-mono break-all">{data.fetch_error}</div>
            <div className="text-xs text-amber-700 mt-1">
              Every role below is marked "unknown" until this is resolved. Check integration-service logs / deployment status.
            </div>
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="With Sage Code" value={stats?.total_with_code ?? 0} tone="text-gray-700" />
        <StatCard label="In Sync" value={stats?.in_sync ?? 0} tone="text-emerald-600" />
        <StatCard label="Out of Sync" value={stats?.out_of_sync ?? 0} tone="text-red-600" />
        <StatCard label="No Linked Title" value={stats?.no_link ?? 0} tone="text-gray-500" />
        <StatCard label="Unknown (F&O)" value={stats?.unknown ?? 0} tone="text-gray-400" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search role code, title, sage code, or TitleId..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="out_of_sync">Out of sync</SelectItem>
            <SelectItem value="in_sync">In sync</SelectItem>
            <SelectItem value="no_link">No linked Title</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading && !data ? (
        <div className="py-12 text-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading sync status...
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          {data?.entries.length === 0
            ? 'No Role Master entries have a Sage title code yet.'
            : 'No entries match the current filters.'}
        </div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50/80">
                <TableHead className="w-[80px] text-xs font-medium">Code</TableHead>
                <TableHead className="min-w-[220px] text-xs font-medium">Title</TableHead>
                <TableHead className="w-[120px] text-xs font-medium">Sage Code</TableHead>
                <TableHead className="text-xs font-medium">Linked Titles</TableHead>
                <TableHead className="w-[140px] text-xs font-medium">Status</TableHead>
                <TableHead className="w-[110px] text-xs font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.map((entry) => {
                const preset = STATE_PRESET[entry.status];
                const isExpanded = expandedEntryIds.has(entry.entry_id);
                const sourceCounts = entry.linked_titles.reduce(
                  (acc, t) => { acc[t.source] = (acc[t.source] || 0) + 1; return acc; },
                  {} as Record<string, number>,
                );
                const sourceSummary: string[] = [];
                if (sourceCounts.rm_direct) sourceSummary.push(`${sourceCounts.rm_direct} direct`);
                if (sourceCounts.via_job) sourceSummary.push(`${sourceCounts.via_job} via job`);

                return (
                  <React.Fragment key={entry.entry_id}>
                    <TableRow
                      ref={(el) => { rowRefs.current[entry.entry_id] = el; }}
                      className={cn(
                        'cursor-pointer hover:bg-gray-50',
                        entry.status === 'out_of_sync' && 'bg-red-50/40',
                        focusEntryId === entry.entry_id && 'ring-2 ring-primary ring-inset',
                      )}
                      onClick={() => toggleExpanded(entry.entry_id)}
                    >
                      <TableCell className="font-mono text-xs text-gray-400 py-2">{entry.role_code}</TableCell>
                      <TableCell className="py-2">
                        <span className="text-sm font-medium text-gray-900">{entry.title}</span>
                      </TableCell>
                      <TableCell className="py-2">
                        <span className="font-mono text-xs text-emerald-700">{entry.rm_sage_code}</span>
                      </TableCell>
                      <TableCell className="py-2">
                        {entry.linked_titles.length === 0 ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <span className="text-xs text-gray-600">
                            {entry.linked_titles.length} Title{entry.linked_titles.length === 1 ? '' : 's'}
                            {sourceSummary.length > 0 && (
                              <span className="text-gray-400"> · {sourceSummary.join(', ')}</span>
                            )}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 gap-1', preset.tone)}>
                          {preset.icon}
                          {preset.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        {entry.status === 'no_link' ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <Button
                            size="sm"
                            variant={entry.status === 'out_of_sync' ? 'default' : 'outline'}
                            onClick={() => handleForceSyncOne(entry.entry_id)}
                            disabled={pushingEntryId === entry.entry_id || isPushing}
                            className="h-7 text-xs"
                          >
                            {pushingEntryId === entry.entry_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>Force Sync</>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded row — per-title breakdown */}
                    {isExpanded && entry.linked_titles.length > 0 && (
                      <TableRow className="bg-gray-50/60">
                        <TableCell colSpan={6} className="py-2 px-4">
                          <div className="space-y-1.5">
                            {entry.linked_titles.map((t) => {
                              const sourceLabel = t.source === 'rm_direct' ? 'Direct link' : 'Via linked Job';
                              const displayCode = t.current_accsi_code === null
                                ? '(unknown — F&O unreachable)'
                                : (t.current_accsi_code || '(blank)');
                              return (
                                <div
                                  key={t.title_id}
                                  className="flex items-center gap-3 text-xs"
                                >
                                  <span
                                    className={cn(
                                      'inline-block h-2 w-2 rounded-full shrink-0',
                                      t.in_sync ? 'bg-emerald-500' : 'bg-red-500',
                                    )}
                                  />
                                  <span className="font-mono text-gray-700 min-w-[220px]">{t.title_id}</span>
                                  <span className="text-gray-400">{sourceLabel}</span>
                                  <span className="text-gray-400">F&O code:</span>
                                  <span
                                    className={cn(
                                      'font-mono',
                                      t.in_sync ? 'text-emerald-700' : 'text-red-700',
                                    )}
                                  >
                                    {displayCode}
                                  </span>
                                  {!t.in_sync && t.current_accsi_code !== null && (
                                    <span className="text-gray-400">→ will become <span className="font-mono text-emerald-700">{entry.rm_sage_code}</span></span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ── Sub-component ────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={cn('text-2xl font-semibold', tone)}>{value}</div>
    </CardContent>
  </Card>
);

export default SageCodeSyncPanel;
